import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DriveSweepModel } from '../../../../types';
import { UserKeys, deleteDriveMonitorsReact } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useState } from 'react';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import SharedDialog from '../../../shared/shared-dialog';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteDriveSweepDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDriveSweeps: DriveSweepModel[];
  changeLogRoles: string[];
  onSuccess?: () => void;
}

export default function DeleteDriveSweepDialog({
  open,
  handleClose,
  selectedDriveSweeps,
  changeLogRoles,
  onSuccess,
}: DeleteDriveSweepDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const sweepIds = selectedDriveSweeps.map((sweep) => sweep.folderId);

  const moreThanOneRule = selectedDriveSweeps.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteDriveMonitorsReact(sweepIds, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Drive Sweep${
          moreThanOneRule ? 's' : ''
        } deleted successfully!`,
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveSweepModels],
      });

      setChangeLogMessage('');
      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue deleting Drive Sweep${
          moreThanOneRule ? 's' : ''
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_sweep.delete_drive_sweep, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSweep,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanDeleteDriveSweep.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete Drive Sweep${moreThanOneRule ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography>
            {moreThanOneRule ? (
              <>
                Delete <strong>{selectedDriveSweeps.length}</strong> sweeps?
              </>
            ) : (
              <>
                Delete <strong>{selectedDriveSweeps[0].name}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: `Delete sweep${moreThanOneRule ? 's' : ''}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
